import { useEffect, useState } from "react";
import verifiedLogo from "../../assets-user/agents/VerifiedBadge.png";
import defaultAvatar from "../../assets-user/agents/default_avatar.png";
import api from "../../configs/api";
import { useNavigate } from "react-router-dom";

const AgentListing = () => {
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const res = await api.get("/agent-builder");
        const list = res.data?.list || [];
        // console.log("list:", list);
        const transformed = list.map((item) => ({
          _id: item._id || item.id,
          name: item.name || item.userId?.fullName || "Unknown",
          role: item.role || item.companyId?.companyName || "Agent / Builder",
          reraId: item.reraId || item.companyId?.reraId || "",
          operatingSince: item.operatingSince || null,
          locations: (item.operatingAreas && item.operatingAreas.length > 0) ? item.operatingAreas.join(", ") : item.location || null,
          deals: (item.dealTypes && item.dealTypes.length > 0) ? item.dealTypes.join(", ") : item.deals || null,
          propertyForSale: item.propertyForSale || item.propertiesForSale || 0,
          dealsClosed: item.dealsClosed || item.dealClosed || 0,
          teamMembers: item.teamMembers || 0,
          image: item.image || item?.userId?.profileImage || defaultAvatar,
          logoImg: item.logoImg || item.companyId?.companyLogo,
          isVerified: item.isVerified || item?.userId?.isVerified,
          worksWithCompany: item.worksWithCompany ?? Boolean(item?.companyId),
        }));

        setAgents(transformed);
      } catch (error) {
        console.error("Failed fetching agents:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAgents();
  }, []);

  return (
    <div className="bg-gray-50 py-6 px-4 sm:px-6 lg:px-10 min-h-screen">
      <div className="grid grid-cols-1 gap-4 sm:gap-6">
        {loading
          ? Array(6)
              .fill(0)
              .map((_, i) => <AgentCardSkeleton key={i} />)
          : agents.map((agent, index) => (
              <AgentCard key={index} agent={agent} navigate={navigate} />
            ))}
      </div>
    </div>
  );
};

export default AgentListing;

const AgentCard = ({ agent, navigate }) => {
  return (
    <div
      className="bg-white rounded-lg shadow-md overflow-hidden 
      transition-all duration-200 hover:shadow-lg "
    >
      <div className="p-4 sm:p-5 lg:p-6">
        <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 w-full">
          <div className="flex gap-4 sm:gap-6 w-full">
            {/* LEFT IMAGE */}
            <div className="flex flex-col items-center ">
              <img
                src={agent.image}
                alt={agent.name}
                className="w-16 h-16 sm:w-24 sm:h-24 rounded-full object-cover border"
              />
              {agent.isVerified && (
                <img alt="" src={verifiedLogo} className="w-16 sm:w-24 mt-1" />
              )}
            </div>

            {/* CENTER SECTION */}
            <div className="flex-1 flex-col tracking-wide">
              <div className="flex flex-col space-y-1 sm:space-y-1.5">
                <h3 className="font-poppins text-lg sm:text-xl md:text-2xl  font-medium">
                  {agent.name}
                </h3>
                <p className="text-xs sm:text-sm text-gray-700">{agent.role}</p>
                {agent.reraId && (
                  <div className="text-xs sm:text-sm">
                    <span className=" text-gray-700 ">RERA ID: </span>
                    <span className="text-gray-500">&nbsp;{agent.reraId}</span>
                  </div>
                )}

                {agent.operatingSince && (
                  <div className="text-xs sm:text-sm ">
                    <span className=" text-gray-700 ">Operating Since: </span>
                    <span className="text-gray-500">
                      &nbsp;{agent.operatingSince}
                    </span>
                  </div>
                )}
              </div>
              {!agent?.worksWithCompany && (
                <div className="flex gap-6  mt-4 sm:mt-5 lg:mt-6">
                  <StatSmall
                    label="Property for Sale"
                    value={agent.propertyForSale}
                  />
                  <StatSmall label="Deals Closed" value={agent.dealsClosed} />
                </div>
              )}
              <div className="flex justify-end md:justify-start gap-3 mt-4 sm:mt-5 lg:mt-6 flex-wrap">
                <button className="px-3 sm:px-4 py-2 border border-red-600 text-red-600 rounded-full text-xs sm:text-sm font-semibold transition-all duration-200 active:scale-95 zhover:bg-red-50">
                  Contact Agent
                </button>

                <button
                  onClick={() => {
                    navigate(`/agents/${agent._id}`);
                    window.scrollTo(0, 0);
                  }}
                  className="px-3 sm:px-4 py-2 bg-red-600 text-white rounded-full text-xs sm:text-sm font-semiboldtransition-all duration-200 active:scale-95 hover:bg-red-700"
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
          {/* RIGHT INFO BOX */}
          <div
            className={`border rounded-lg lg:max-w-sm w-full  ${
              !agent?.worksWithCompany
                ? "flex flex-col max-h-fit"
                : "grid grid-cols-1"
            }  p-3 sm:p-4 space-y-2 sm:space-y-4`}
          >
            <div>
              <p className="text-xs sm:text-sm font-medium">Operating in:</p>
              <p className="text-[0.7rem] sm:text-xs text-gray-600 mt-1 line-clamp-3">
                {agent.locations}
              </p>
            </div>
            <div>
              <p className="text-xs sm:text-sm font-medium">Deals in:</p>
              <p className="text-[0.7rem] sm:text-xs text-gray-600 mt-1 line-clamp-3">
                {agent.deals}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* BOTTOM SECTION */}
      {agent?.worksWithCompany && (
        <div className="border-t bg-[#F4E3D9] p-4 sm:p-5 md:p-6">
          <div className="flex  items-center gap-4 sm:gap-6">
            <div className="sm:w-24 flex-shrink-0 flex items-center justify-center">
              <img
                alt=""
                src={agent.logoImg}
                className=" w-14 h-14 lg:w-20 lg:h-20 aspect-square rounded-lg overflow-hidden object-cover"
              />
            </div>

            <div className="grid grid-cols-3 flex-wrap gap-2 sm:gap-6">
              <StatSmall
                label="Properties for Sale"
                value={agent.propertyForSale}
              />
              <StatSmall label="Deals Closed" value={agent.dealsClosed} />
              <StatSmall label="Team Members" value={agent.teamMembers} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AgentCardSkeleton = () => (
  <div className="bg-white rounded-lg shadow-md overflow-hidden p-4 sm:p-5 lg:p-6 animate-pulse">
    <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 w-full">
      {/* LEFT IMAGE SECTION */}
      <div className="flex gap-4 sm:gap-6 w-full">
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 sm:w-24 sm:h-24 rounded-full bg-gray-300" />
          <div className="w-16 sm:w-24 h-4 mt-2 bg-gray-200 rounded" />
        </div>

        {/* CENTER TEXT SECTION */}
        <div className="flex-1 space-y-2 sm:space-y-3">
          <div className="h-5 sm:h-6 w-40 bg-gray-300 rounded" />
          <div className="h-4 w-28 bg-gray-200 rounded" />

          <div className="h-3 w-36 sm:w-40 bg-gray-200 rounded" />
          <div className="h-3 w-32 sm:w-36 bg-gray-200 rounded" />

          {/* Property & Deals stats */}
          <div className="flex gap-6 mt-3 sm:mt-4 lg:mt-5">
            <div className="space-y-2">
              <div className="h-5 w-12 bg-gray-300 rounded" />
              <div className="h-4 w-20 bg-gray-200 rounded" />
            </div>
            <div className="space-y-2">
              <div className="h-5 w-12 bg-gray-300 rounded" />
              <div className="h-4 w-20 bg-gray-200 rounded" />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 mt-4 sm:mt-5 flex-wrap">
            <div className="h-8 sm:h-9 w-28 bg-gray-300 rounded-full" />
            <div className="h-8 sm:h-9 w-28 bg-gray-300 rounded-full" />
          </div>
        </div>
      </div>

      {/* RIGHT INFO BOX */}
      <div className="border rounded-lg lg:max-w-sm w-full p-3 sm:p-4 space-y-4">
        <div>
          <div className="h-4 w-24 bg-gray-200 rounded" />
          <div className="h-3 w-full bg-gray-200 rounded mt-2" />
        </div>
        <div>
          <div className="h-4 w-24 bg-gray-200 rounded" />
          <div className="h-3 w-full bg-gray-200 rounded mt-2" />
        </div>
      </div>
    </div>

    {/* BOTTOM COMPANY SECTION */}
    <div className="border-t bg-[#F4E3D9] mt-4 sm:mt-5 lg:mt-6 p-4 sm:p-5 md:p-6">
      <div className="flex items-center gap-4 sm:gap-6">
        {/* Company Logo */}
        <div className="w-14 h-14 lg:w-20 lg:h-20 rounded-lg bg-gray-300" />

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 sm:gap-6">
          <div className="space-y-2">
            <div className="h-5 w-10 bg-gray-300 rounded" />
            <div className="h-4 w-20 bg-gray-200 rounded" />
          </div>
          <div className="space-y-2">
            <div className="h-5 w-10 bg-gray-300 rounded" />
            <div className="h-4 w-20 bg-gray-200 rounded" />
          </div>
          <div className="space-y-2">
            <div className="h-5 w-10 bg-gray-300 rounded" />
            <div className="h-4 w-20 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

const StatSmall = ({ label, value }) => (
  <div>
    <div className="text-base sm:text-2xl font-medium">{value}</div>
    <div className="text-xs sm:text-sm text-gray-600">{label}</div>
  </div>
);
